import { IconProp } from '@fortawesome/fontawesome-svg-core';
import React, { useState, useEffect, useRef } from 'react';
import Button from './Button';
import Icon from './Icon';
import { useScrollDirection } from 'react-use-scroll-direction';
import { useWindowScroll } from '../providers/UseScrollY';
import { useGlobalControls } from '../context/GlobalControlsContext';

const SCROLLING_BREAKPOINT = 75;
const SCROLLING_MIN_DELTA = 50;

type FiltersProps = {
  title: string;
  search?: string;
  onSearch?: (search: string) => void;
  staticButtons?: string[];
  keys?: string[];
  onFilterChange?: (filters: string[]) => void;
  labelExtractor?: (segment: string) => string;
  rightSide?: React.ReactNode;
  icon?: IconProp;
};

function Filters(props: FiltersProps) {
  const { title, staticButtons, keys, onFilterChange, labelExtractor, rightSide, icon } = props;

  const filtersRef = useRef<HTMLDivElement>(null);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  function handleButtonClick(label: string) {
    setActiveFilters(prev =>
      prev.includes(label) ? prev.filter(item => item !== label) : [...prev, label]
    );
  }

  function clearFilters() {
    setActiveFilters([]);
  }

  useEffect(() => {
    if (onFilterChange) onFilterChange(activeFilters);
  }, [activeFilters, onFilterChange]);

  const scrollDir = useScrollDirection();
  const scrollAmt = useWindowScroll();
  const { scrollLocked } = useGlobalControls();

  const [slimHeader, setSlimHeader] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [ignoreScroll, setIgnoreScroll] = useState(false);

  useEffect(() => {
    setIgnoreScroll(true);

    if (timer) clearTimeout(timer);

    setTimer(
      setTimeout(() => {
        setIgnoreScroll(false);
      }, 1000)
    ); // adjust ms to cover lock/unlock duration
  }, [scrollLocked]);

  useEffect(() => {
    if (ignoreScroll) return;

    if (scrollDir.isScrolling) {
      if (scrollAmt < SCROLLING_BREAKPOINT) {
        setSlimHeader(false);
        setLastScrollY(scrollAmt);
      } else if (
        scrollDir.isScrollingDown &&
        Math.abs(scrollAmt - lastScrollY) > SCROLLING_MIN_DELTA
      ) {
        setSlimHeader(true);
        setLastScrollY(scrollAmt);
      } else if (
        scrollDir.isScrollingUp &&
        Math.abs(scrollAmt - lastScrollY) > SCROLLING_MIN_DELTA
      ) {
        setSlimHeader(false);
        setLastScrollY(scrollAmt);
      }
    }
  }, [scrollDir]);

  return (
    <div
      ref={filtersRef}
      className={`navegante-filters filters-block pt-4 mb-2 pb-4 border-b border-t sticky w-full z-10 transition-all duration-500`}
      style={{
        marginTop: -1,
        top: 76,
        ...((filtersRef.current?.clientHeight ?? 0) > 77 && slimHeader
          ? { transform: 'translateY(-77px)' }
          : { transform: 'translateY(0px)' }),
      }}
    >
      <div className="px-4 flex justify-between items-center">
        <div className="text-lg font-semibold flex items-center filter-title">
          {icon && <Icon icon={icon} className="mr-2" />}
          <span>{title}</span>
        </div>
        {rightSide}
      </div>
      {keys && keys.length > 0 && (
        <div
          className={`text-sm hide-scrollbar flex flex-row space-x-2 mt-4 overflow-x-auto px-4${
            activeFilters.length < 1 ? ' -ml-2' : ''
          }`}
        >
          <Button
            outline="border-blue-500"
            shape="oval"
            icon={['far', 'xmark']}
            iconPosition="left"
            className={`bg-blue-500 text-white flex-shrink-0 transition-all duration-500${
              activeFilters.length > 0
                ? ' opacity-100 filter-on-active'
                : ' opacity-0 -mt-97 filter-on'
            }`}
            style={{ width: '100px' }}
            onClick={clearFilters}
          >
            Filters: {activeFilters.length}
          </Button>
          {staticButtons?.map(label => (
            <Button
              key={label}
              outline="border-black"
              shape="oval"
              className={`transition-all duration-500 ${
                activeFilters.includes(label)
                  ? 'bg-black text-white filter-tags-active'
                  : 'text-black filter-tags'
              }`}
              onClick={() => handleButtonClick(label)}
            >
              {label}
            </Button>
          ))}

          {keys?.map(keys => (
            <Button
              key={keys}
              outline="border-black"
              shape="oval"
              className={`transition-all duration-500 ${
                activeFilters.includes(keys)
                  ? 'bg-black text-white filter-tags-active'
                  : 'text-black filter-tags'
              }`}
              onClick={() => handleButtonClick(keys)}
            >
              {labelExtractor ? labelExtractor(keys) : ''}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export default Filters;
